import { PathName } from 'router/paths';

// Zoom Contact Center web chat client (LMD-834). The script renders Zoom's own
// floating chat bubble. URL and env match the Embed Web Tag that Zoom's admin
// console generates for LifeMD's account (us01 = Zoom's US cluster).
//
// `zcc-sdk.js` is the campaign entry point; `chat-client.js` is the per-entry-point
// one this integration used first. Both URLs serve a byte-identical loader (same
// SHA-256, same 160,252 bytes, same five host hooks), so moving between them changes
// which Zoom object we bind to, not the SDK.
export const ZOOM_CHAT_SCRIPT_SRC = 'https://us01ccistatic.zoom.us/us01cci/web-sdk/zcc-sdk.js';
export const ZOOM_CHAT_ENV = 'us01';

/**
 * Identifies the Zoom account this page belongs to.
 *
 * Account-level, not per campaign: both of LifeMD's web campaigns hand out this same
 * value, so which campaign serves a page is decided Zoom-side by Campaign Location URL
 * matching rather than by anything we embed. Nothing here changes to move a build between
 * them — the hostname it is served from decides, and every environment has its own:
 *
 *   production      care.lifemd.com
 *   dev             care.dev.lifemd.com
 *   stage           care.stage.lifemd.com
 *   hotfix          care.hotfix.lifemd.com
 *
 * Plus three preview-channel hosts, because `build.yml`'s Firebase target is chosen from the PR's
 * BASE ref, not from `develop` alone — so a promote-to-main or stage PR previews on a different
 * site than a develop PR does:
 *
 *   base develop            lifemd-frontend-develop--<pr>-<hash>.web.app
 *   base stage              lifemd-test--<pr>-<hash>.web.app
 *   base main | hotfixes    lifemd-frontend-hotfixes--<pr>-<hash>.web.app
 *
 * Note the four portal hosts are custom domains that do NOT contain their Firebase site id, so a
 * rule written against a site id matches preview channels only and leaves the portals themselves
 * with no campaign at all. A host matching neither campaign gets no bubble.
 *
 * Those rules are in place as of Zoom-side work on 2026-08-11, and the two campaigns are disjoint
 * (verified 2026-08-12): `MSR Support Chat` matches `care.lifemd.com` and nothing else, while
 * `LifeMD - Live Chat DEV 1` carries the non-production predicates. Production traffic therefore
 * cannot reach the dev campaign, and `localhost` — in neither set — gets no bubble at all.
 *
 * Committed rather than held as a secret because it cannot be confidential in any
 * implementation. Zoom's SDK reads it from this DOM attribute, copies it into query
 * strings and sends it as an `X-Zm-apiKey` header; Vite inlines every `VITE_*` value into
 * the bundle at build time regardless, which is why Vite's own documentation says those
 * must not hold sensitive values. Nothing on its path is signed, so it authorises nothing
 * — it is a routing identifier. Verified identity is carried separately by the RS256 token
 * minted in `convex/zoomConsumerAuth.ts`, which Zoom checks against a public key
 * registered in Contact Center admin.
 */
export const ZOOM_CHAT_CAMPAIGN_API_KEY = 'zaUvLlRPRRSSvXYxK_gwoA';

/**
 * The global the SDK reads its host-page configuration from. Note the `Config` suffix:
 * an earlier implementation targeted `window.zoomCampaignSdk`, which does not exist, so
 * the identity call silently never ran. Verified against Zoom's shipped bundle, which
 * resolves every host hook via `window.zoomCampaignSdkConfig`.
 */
export const ZOOM_CHAT_CONFIG_GLOBAL = 'zoomCampaignSdkConfig';

/**
 * The window event the campaign runtime dispatches the moment it publishes
 * `window.zoomCampaignSdk`, which is the earliest point its methods and events can be used.
 *
 * There is a later `zoomCampaignSdk:ready` event too, plus a `postMessage({ type:
 * 'CampaignSDKReady' })`, both fired once chat, video and scheduled-callback init settle. We wait
 * on `:init` and then read the `isReady` getter instead, because a getter answers for a state that
 * has already been reached; an event only ever announces the transition, so a late subscriber
 * learns nothing. See zoomChatSdk.ts for provenance.
 */
export const ZOOM_CHAT_SDK_INIT_EVENT = 'zoomCampaignSdk:init';

/**
 * The window event the campaign runtime dispatches once chat, video and scheduled-callback init
 * have all settled. The readiness check above reads a getter instead, but the bubble's show and hide
 * have to be tried again at exactly this moment: Zoom's calls may do nothing or throw before it, and
 * the getter only answers when asked, not when the state flips. See useZoomChatBubbleVisibility.
 */
export const ZOOM_CHAT_SDK_READY_EVENT = 'zoomCampaignSdk:ready';

/**
 * Fired on the campaign SDK's own emitter — not on `window` — when an engagement opens, which is
 * the moment an agent starts talking to this browser. Subscribed via `sdk.on(...)`.
 *
 * The runtime forwards its active campaign's chat events onto the public emitter wholesale, so the
 * name here is the chat client's own: `open`, `close`, `show`, `hide`, `send_message`,
 * `engagement_started` and `engagement_ended` all arrive by that route.
 */
export const ZOOM_CHAT_ENGAGEMENT_STARTED_EVENT = 'engagement_started';

export const ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT = 'engagement_ended';

/**
 * Fired on the same emitter when the patient closes Zoom's chat window. Zoom's reference says the
 * bubble cannot be hidden or shown while a chat window is open, so closing it is the moment a hide
 * that failed can be tried again. The name is in the runtime's forwarded list, read out of
 * web-campaign.js on 2026-10-29; that it fires when the patient closes the window, and not only
 * on a close made by code, has not been run against a live campaign.
 */
export const ZOOM_CHAT_WINDOW_CLOSED_EVENT = 'close';

/**
 * How long the SDK gets to come up before a silent dark surface is reported.
 *
 * Deliberately generous. Nothing user-facing hangs off this — it decides only when to log — and the
 * runtime is a ~1.2 MB dynamic import on top of the loader, so a slow connection legitimately takes
 * many seconds. A tighter bound would trade a real signal for a stream of false ones.
 */
export const ZOOM_CHAT_SDK_READY_TIMEOUT_MS = 30_000;

/**
 * How long after an engagement opens we wait before concluding no identity was supplied.
 *
 * A grace period rather than an immediate check, because the SDK may request the credential as
 * PART of starting the engagement: the two are concurrent, and their order is Zoom's to choose. An
 * immediate check would therefore report a healthy chat as unidentified roughly whenever the
 * credential request lost that race. The same length is how far before the engagement's start event
 * a credential request still counts as that engagement's own.
 */
export const ZOOM_CHAT_IDENTITY_GRACE_MS = 5_000;

/**
 * Turns the widget on for a single page load outside the live environment.
 *
 * The `zoom-chat-widget` flag fails closed, which is right for production but means a preview shows
 * no widget at all until the flag exists in Unleash and is enabled for that environment — so the
 * feature could not be demonstrated or reviewed. This override makes a preview testable without
 * weakening the production switch.
 *
 * Ignored in the live environment, so it can never resurrect a channel that has been deliberately
 * switched off for patients.
 */
export const ZOOM_CHAT_ENABLE_OVERRIDE_PARAM = 'zoomChat';

/**
 * How long an escalation waits for the campaign runtime before reporting failure to its caller.
 *
 * Much tighter than ZOOM_CHAT_SDK_READY_TIMEOUT_MS, and for the opposite reason: that one only
 * decides when to log, this one holds up a patient who just asked for a human. If the runtime is
 * not up in this long, the caller needs the failure so it can offer the fallback path.
 */
export const ZOOM_CHAT_ESCALATION_SDK_TIMEOUT_MS = 15_000;

/**
 * How long an escalation waits for a chat it is ending to report `engagement_ended` before it
 * gives up on replacing it: the open chat is brought up and the tap reports replace-failed (stage,
 * Oct 1: `endChat()` resolved and the event followed within a second). Bounded so a runtime that
 * never reports the end cannot leave the Connect tap hanging.
 */
export const ZOOM_CHAT_ESCALATION_END_TIMEOUT_MS = 5_000;

/**
 * localStorage keys the escalation writes before opening the widget. This is the channel the ZCC
 * dev campaign's custom variables read via their "From website data → Local storage" binding —
 * the flow's `escalation`/`summaryHint`/`firstMessage` variables have item keys set to exactly
 * these. `threadId` is no longer written (it served only the flow's pull from
 * /escalation-summary, which V1 does not have) but stays listed so every sweep still clears a
 * value an older build left behind. Namespaced `lifemd_` to avoid colliding with Zoom's own
 * localStorage entries.
 */
export const ZOOM_CHAT_WEBSITE_DATA_KEYS = {
  // Not read by the flow: which escalation wrote the hints, so a failed open clears only its own.
  attempt: 'lifemd_escalationAttempt',
  escalation: 'lifemd_escalation',
  firstMessage: 'lifemd_firstMessage',
  summaryHint: 'lifemd_summaryHint',
  threadId: 'lifemd_threadId'
} as const;

/**
 * Zoom SDK state, all in localStorage. These survive `session.clear()` on logout, so without
 * clearing them a patient's chat identity outlives their session on a shared device.
 *
 * ⚠️ This list is empirical and was derived against the per-entry-point binding
 * (`chat-client.js` + `data-chat-entry-id`), before the switch to the campaign tag. The two
 * loaders are byte-identical, so the same names are likely, but that is an inference — the SDK
 * demonstrably composes at least one key name at runtime (see JWT_SHAPED_VALUE below), so a
 * campaign-route handle under an unlisted name is not excluded.
 *
 * `claimZoomChatIdentity` no longer needs this list to DECIDE to clear — an owner marker naming a
 * different patient is sufficient. It still needs it to REMOVE anything: a name outside this list,
 * the campaign prefixes below and the JWT value-sweep survives the clear, and a reload does not
 * touch localStorage, so the next mount finds a matching owner and leaves it. **Verifying the
 * combined set against the campaign tag on a signed-in preview is therefore a pre-ship gate, not a
 * follow-up** — a run shows the keys the flows it exercises actually write.
 *
 * First run: 2026-08-07, on the PR preview against campaign runtime `web-campaign.js` v=9773. An
 * active engagement persisted and RESTORED across a document reload through this list alone —
 * `livesdk_persisted_state` appeared at engagement start, and neither `zcx-` prefix was written by
 * this campaign's flow (the prefixes stay in the sweep as defense: the runtime defines them, and a
 * config or runtime change could activate them). Logout swept the whole set, after which the
 * write-back race re-persisted `livesdk_persisted_data` plus one JWT credential key — unattributed,
 * so the next patient's mount took the probe path and produced a fresh consumer id with no restored
 * transcript, verified agent-side. That observation is what motivated coupling the owner stamp to
 * cleanup success. Repeat the run when Zoom's unversioned loader changes what it serves.
 *
 * `ZoomWebsdkInstanceId` is the one that matters most, and it is not obvious why. It is the
 * handle Zoom uses to re-attach this browser to a consumer record it holds server-side.
 * Clearing only the `livesdk_*` keys leaves it behind, and the next visitor is then re-attached
 * to the previous patient: the agent sees the previous patient's name and email, and the
 * previous conversation is restored into the widget. Verified by A/B on the deployed preview —
 * clearing the `livesdk_*` keys alone reuses the same consumer id, clearing this one as well
 * yields a fresh consumer and no transcript. Enabling Zoom's Consumer Authentication does not
 * cover this: the signed token corrects the email field only, not the record or the transcript.
 */
export const ZOOM_CHAT_STORAGE_KEYS: ReadonlyArray<string> = [
  'ZoomWebsdkInstanceId',
  'livesdk_persisted_data',
  'livesdk_persisted_state',
  '__livesdk_chat_zdt_monitor'
] as const;

/**
 * The campaign runtime's persisted-session key families, matched by prefix because the middle
 * segments are composed at runtime.
 *
 * Unlike the list above, these are not empirical: they are read out of Zoom's own campaign
 * runtime (`web-campaign.js`, v=9773, fetched 2026-08-07), which ships with its comments intact:
 *
 *   zcx-customer-chat/{persistKey}/{apikey}          — the campaign chat's persisted store
 *   zcx-persisted-session/{persistKey}/activeSession — session persistence
 *
 * Both are localStorage-backed. The runtime restores a stored session while its timestamp is
 * under 24 hours old — or, on the `state.activeSessionId` branch, with no freshness check at
 * all. So a patient's active session under these names survives logout, tab close and our
 * eviction reload, ready to be restored for whoever signs in next. Neither name is in
 * ZOOM_CHAT_STORAGE_KEYS and the values are JSON state, not JWT-shaped, so both existing sweeps
 * miss them. Surfaced by external review of the campaign runtime (PR #10898 re-review 13) and
 * confirmed against the same file.
 *
 * Deliberately narrow: prefix removal does not touch the SDK bootstrap keys
 * (`is_websdk_cache_cleared`, `OP/*`) whose removal half-broke the widget under the
 * per-entry-point binding — see the do-not-widen note in clearZoomChatIdentity.ts.
 */
export const ZOOM_CHAT_STORAGE_KEY_PREFIXES: ReadonlyArray<string> = [
  'zcx-customer-chat/',
  'zcx-persisted-session/'
] as const;

/**
 * Records which patient the Zoom keys above currently belong to, so a mismatch can be detected
 * at widget mount.
 *
 * This exists because the teardown middleware can only fire on `user/clearUser`, and the most
 * ordinary way a session ends never dispatches it: portal tokens live in sessionStorage, so
 * closing the tab ends the session silently. The Zoom keys are in localStorage and survive. The
 * next patient to sign in on that browser would otherwise be re-attached to the previous
 * patient's consumer record — the same disclosure ZoomWebsdkInstanceId caused, reached by a
 * different route. Comparing a stamped owner at mount covers every path, including the ones that
 * end without any teardown at all.
 */
export const ZOOM_CHAT_IDENTITY_OWNER_KEY = 'lifemd_zoom_chat_identity_owner';

/**
 * The marker value format, versioned. A marker is trusted only when it carries the current
 * version prefix, because a marker attests the cleanup that was in force when it was stamped —
 * markers from earlier builds of this branch were written over state whose key families that
 * build's probe could not see (the campaign prefixes joined the sweep later), so trusting one
 * lets exactly that state ride a matching owner past the probe forever. A legacy or unrecognised
 * value therefore reads as unattributed: the probe runs, today's sweep clears what the old one
 * missed, and the marker is re-stamped in the current format.
 *
 * No production browser holds a legacy marker — the marker never shipped outside this branch's
 * preview channels — so this is hygiene for tester browsers and the template for the future:
 * bump the version whenever the removal set widens.
 */
export const ZOOM_CHAT_IDENTITY_OWNER_VERSION = 'v2:';

/**
 * Matches a JWT-shaped value. Zoom stores its own session tokens under an unpredictably-named
 * key, so they are found by value rather than by name.
 *
 * Note this never matches our identity token: the SDK hands that straight to Zoom and does not
 * persist it (confirmed with Consumer Authentication both off and on — the only JWTs in
 * localStorage are Zoom's own HS256 and ES256 tokens). It is Zoom's session that has to go.
 */
export const JWT_SHAPED_VALUE = /eyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\./;

// Denylist: the bubble shows portal-wide EXCEPT these routes. A live video visit must never get a
// chat bubble over it, and checkout, intake, identity and other step-by-step flows shouldn't be
// interrupted mid-step. (Mobile-app WebViews are excluded separately via the flutter_inappwebview
// bridge check.)
//
// This list is now the only thing that hides the bubble. The chat is mounted beside the router, not
// per layout, so it no longer depends on which layout a page sits in: a route outside the Main
// layout that is not listed here shows the bubble, and a NEW route of that kind has to be added here
// to stay hidden. A match is the exact path or the path followed by "/", so `/intake-form` does not
// cover `/intake-form-mens-trt`, and `/prescriptions` does not cover `/my-prescriptions`.
//
// The signed-out pages are listed too. The signed-in gate keeps the bubble off them, but it does not
// keep the script off: a sign-in finishes on one of them (the WorkOS callback has the patient set
// before it moves on), and the script is injected from the first page that is not listed. For the
// same reason the redirect aliases of `/create-appointment` are listed. A fresh load sees the alias
// on its first render, before the redirect, and would release the script on a page that is hidden.
export const HIDDEN_ZOOM_CHAT_PATHS: ReadonlyArray<string> = [
  PathName.AddMeasurement,
  PathName.AppointmentRequired,
  PathName.Checkout,
  PathName.ConfirmAccount,
  PathName.CreateAppointment,
  PathName.CreateFreeAccount,
  PathName.CreateNewPassword,
  PathName.CurrentWeight,
  PathName.DeviceStatus,
  PathName.ForgotPassword,
  PathName.IntakeForm,
  PathName.IntakeFormMensTRT,
  PathName.Join,
  PathName.Login,
  PathName.MedicareConversion,
  PathName.MedicationConfirmation,
  PathName.MigrateToPlan,
  PathName.MigrateToTT,
  PathName.MigrateToWM,
  PathName.MoveFromSemaglutide,
  PathName.MoveFromTirzepatide,
  PathName.PaymentMethods,
  PathName.PostOnboarding,
  PathName.PostOnboardingWH,
  PathName.Prescriptions,
  PathName.SelectPrescriptions,
  PathName.SignUp,
  PathName.Survey,
  PathName.UnsupportedAge,
  PathName.VerifyEmail,
  PathName.VerifyIdentity,
  PathName.VerifyYourIdentity,
  PathName.ZoomCall,
  // Not in PathName: the two routes that redirect to /create-appointment are written as literals.
  '/appointments/create-appointment-extended',
  '/create-appointment-extended'
] as const;
